import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../App';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Trophy, Crown, Medal, CalendarBlank, GraduationCap, Confetti, Users } from '@phosphor-icons/react';

/**
 * Admin — Member of the Month leaderboard.
 * Aggregates non-cancelled activity for the selected month:
 *   • regular bookings  (any court reservation the member made)
 *   • trainings          (lessons booked with a trainer)
 *   • mixins             (community events the member joined)
 *
 * Admin can tune the weight of each activity to shape the ranking (e.g.
 * "reward Mixin participation" → mixin=2, others=1).
 */
const MemberOfTheMonth = () => {
  const { API, language } = useAuth();
  const L = (en, de) => (language === 'de' ? de : en);

  const currentMonth = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(currentMonth);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weights, setWeights] = useState({ regular: 1, training: 1, mixin: 1 });
  const [savingWeights, setSavingWeights] = useState(false);
  const [totalPlayers, setTotalPlayers] = useState(0);

  useEffect(() => {
    load();
  }, [month]);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await API.get(`/admin/member-of-the-month?month=${month}&limit=50`);
      setRows(data?.rows || []);
      setWeights(data?.weights || { regular: 1, training: 1, mixin: 1 });
      setTotalPlayers(data?.total_players || 0);
    } catch (e) {
      toast.error(L('Failed to load leaderboard', 'Ranking konnte nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const saveWeights = async () => {
    try {
      setSavingWeights(true);
      await API.put('/admin/member-of-the-month/settings', weights);
      toast.success(L('Weights updated', 'Gewichtung gespeichert'));
      load();
    } catch (e) {
      toast.error(L('Failed to save weights', 'Speichern fehlgeschlagen'));
    } finally {
      setSavingWeights(false);
    }
  };

  const monthLabel = useMemo(() => {
    try {
      const [y, m] = month.split('-');
      const d = new Date(parseInt(y), parseInt(m) - 1, 1);
      return d.toLocaleDateString(language === 'de' ? 'de-CH' : 'en-GB', { month: 'long', year: 'numeric' });
    } catch {
      return month;
    }
  }, [month, language]);

  const winner = rows[0];
  const podium = rows.slice(0, 3);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="member-of-the-month-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
            <Crown className="text-amber-500 w-8 h-8" weight="fill" />
            {L('Member of the Month', 'Member of the Month')}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {L(
              'Who plays the most in your center — combined ranking of bookings, trainings and community events.',
              'Wer spielt am meisten im Center — kombiniertes Ranking aus Buchungen, Trainings und Community-Events.'
            )}
          </p>
        </div>
        <div>
          <Label htmlFor="month-picker" className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            {L('Month', 'Monat')}
          </Label>
          <Input
            id="month-picker"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-44"
            data-testid="month-picker"
          />
        </div>
      </motion.div>

      {/* Weights configuration */}
      <Card className="p-4 border-slate-200 bg-slate-50" data-testid="weights-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-slate-900 font-bold text-sm">{L('Activity weights', 'Aktivitäts-Gewichtung')}</p>
            <p className="text-slate-500 text-xs">
              {L('Change how each activity contributes to the score', 'So beeinflusst jede Aktivität den Score')}
            </p>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <WeightInput label={L('Booking', 'Buchung')} value={weights.regular}
              onChange={(v) => setWeights({ ...weights, regular: v })}
              testid="weight-regular" icon={<CalendarBlank />} />
            <WeightInput label={L('Training', 'Training')} value={weights.training}
              onChange={(v) => setWeights({ ...weights, training: v })}
              testid="weight-training" icon={<GraduationCap />} />
            <WeightInput label={L('Mixin', 'Mixin')} value={weights.mixin}
              onChange={(v) => setWeights({ ...weights, mixin: v })}
              testid="weight-mixin" icon={<Confetti />} />
            <Button onClick={saveWeights} disabled={savingWeights} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-weights-btn">
              {savingWeights ? L('Saving…', 'Speichere…') : L('Save & recompute', 'Speichern & neu berechnen')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Winner spotlight */}
      {winner && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
          className="rounded-2xl p-6 bg-gradient-to-br from-amber-400 via-amber-500 to-yellow-600 text-white shadow-xl shadow-amber-500/30 flex items-center gap-5"
          data-testid="winner-spotlight"
        >
          <div className="w-20 h-20 rounded-full bg-white/25 backdrop-blur flex items-center justify-center border-2 border-white/60">
            <Crown weight="fill" size={44} className="text-yellow-100" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white/85 text-xs font-bold uppercase tracking-widest">{L('Member of', 'Member of')} {monthLabel}</p>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight truncate" data-testid="winner-name">{winner.name}</h2>
            <p className="text-white/80 text-sm">{winner.email}</p>
            <div className="flex flex-wrap gap-3 mt-3">
              <StatChip icon={<CalendarBlank />} label={L('Bookings', 'Buchungen')} value={winner.regular_count} />
              <StatChip icon={<GraduationCap />} label={L('Trainings', 'Trainings')} value={winner.training_count} />
              <StatChip icon={<Confetti />} label={L('Mixins', 'Mixins')} value={winner.mixin_count} />
              <StatChip icon={<Trophy weight="fill" />} label={L('Score', 'Score')} value={winner.score} highlight />
            </div>
          </div>
        </motion.div>
      )}

      {/* Full ranking table */}
      <Card className="border-slate-200 overflow-hidden" data-testid="leaderboard-table">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-900">{L('Full Ranking', 'Komplettes Ranking')}</h3>
            <p className="text-slate-500 text-xs">
              {L(`${totalPlayers} active players`, `${totalPlayers} aktive Spieler`)}
            </p>
          </div>
          <Users className="text-slate-400" size={20} />
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">{L('Loading…', 'Lädt…')}</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">
            {L('No activity recorded for this month yet.', 'Keine Aktivität in diesem Monat.')}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => (
              <div key={r.user_id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50" data-testid={`leaderboard-row-${r.rank}`}>
                <RankBadge rank={r.rank} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 truncate">{r.name}</span>
                    {r.is_member && (
                      <Badge className="bg-emerald-500 text-white text-[9px] uppercase tracking-wider">Member</Badge>
                    )}
                  </div>
                  <p className="text-slate-500 text-xs truncate">{r.email}</p>
                </div>
                <div className="hidden sm:flex items-center gap-3 text-xs">
                  <ActivityCount label={L('Book.', 'Buch.')} value={r.regular_count} color="text-sky-600" />
                  <ActivityCount label={L('Train.', 'Train.')} value={r.training_count} color="text-violet-600" />
                  <ActivityCount label={L('Mix.', 'Mix.')} value={r.mixin_count} color="text-rose-600" />
                </div>
                <div className="w-16 text-right">
                  <p className="text-slate-900 font-black text-lg leading-none" data-testid={`leaderboard-score-${r.rank}`}>{r.score}</p>
                  <p className="text-slate-400 text-[10px] uppercase tracking-wider mt-0.5">{L('Score', 'Score')}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

const WeightInput = ({ label, value, onChange, testid, icon }) => (
  <div className="flex flex-col items-start">
    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
      <span className="text-slate-400">{icon}</span>
      {label}
    </Label>
    <Input
      type="number"
      min="0"
      max="100"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value || 0))}
      className="w-20 text-center font-bold"
      data-testid={testid}
    />
  </div>
);

const StatChip = ({ icon, label, value, highlight }) => (
  <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${
    highlight ? 'bg-white text-amber-600 font-black' : 'bg-white/20 text-white'
  }`}>
    <span className="opacity-80">{icon}</span>
    <span className="text-xs font-semibold">{label}</span>
    <span className="font-black">{value}</span>
  </div>
);

const RankBadge = ({ rank }) => {
  if (rank === 1) return <Crown weight="fill" className="w-8 h-8 text-amber-500 shrink-0" />;
  if (rank === 2) return <Medal weight="fill" className="w-8 h-8 text-slate-400 shrink-0" />;
  if (rank === 3) return <Medal weight="fill" className="w-8 h-8 text-amber-700 shrink-0" />;
  return (
    <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 font-bold text-sm flex items-center justify-center shrink-0">
      {rank}
    </div>
  );
};

const ActivityCount = ({ label, value, color }) => (
  <div className="flex flex-col items-center">
    <span className={`font-bold ${color}`}>{value}</span>
    <span className="text-slate-400 text-[10px] uppercase tracking-wider">{label}</span>
  </div>
);

export default MemberOfTheMonth;
